import "../Overview/EndpointOverview.css";
import "../Overview/EndpointDataSections.css";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import { Search, FileText, User, CalendarIcon, RefreshCw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Calendar } from "../../ui/calendar";
import { format } from "date-fns";
import { cn } from "../../../utils/utils";
import TenantLists from "../TenantLists";
import { getPlatformlogs } from "./Api/platformauditlogsapi";
import { ServerAuditLog } from "./interface";
import { Monitor } from "react-feather";
import EmptyStateCard from "../EmptyStateCard";
import OverviewCardHeader from "../Overview/OverviewCardHeader";
import CardPagination from "../CardPagination";
import {
    LIST_STATE_KEYS,
    parsePersistedDate,
    parsePersistedNumber,
    parsePersistedString,
    readPersistedListState,
    serializePersistedDate,
    writePersistedListState,
    useSkipInitialPageReset,
} from "../../../utils/listPaginationState";

export default function PlatformAuditLogsDetails() {
    const savedState = readPersistedListState(LIST_STATE_KEYS.platformAuditLogs);
    const [searchTerm, setSearchTerm] = useState(parsePersistedString(savedState.searchTerm));
    const [startDate, setStartDate] = useState<Date | undefined>(parsePersistedDate(savedState.startDate));
    const [endDate, setEndDate] = useState<Date | undefined>(parsePersistedDate(savedState.endDate));
    const [selectedTenant, setSelectedTenant] = useState(parsePersistedString(savedState.selectedTenant));
    const [serverAuditLogs, setServerAuditLogs] = useState<ServerAuditLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(parsePersistedNumber(savedState.currentPage, 1));
    const PAGE_SIZE = 50;
    const runTenantChangeWithPageReset = useSkipInitialPageReset(setCurrentPage);
    const skipFilterPageResetRef = useRef(true);
    const tableScrollRef = useRef<HTMLDivElement | null>(null);

    const fetchPlatformLogs = async (tenantName: string) => {
        setLoading(true);
        try {
            const response: ServerAuditLog[] = await getPlatformlogs(tenantName);
            setServerAuditLogs(response || []);
        } catch (err) {
            console.error("Error fetching platform logs:", err);
            setServerAuditLogs([]);
        } finally {
            setLoading(false);
        }
    };


    const getActionKeyword = (action: string): { keyword: string; variant: "success" | "destructive" | "warning" | "info" | "default" } | null => {
        const actionLower = action.toLowerCase();
        console.log("Determining keyword for action:", action);


        if (actionLower.includes("approved") || actionLower.includes("added")) {
            return { keyword: "APPROVED", variant: "success" };
        }
        if (actionLower.includes("removed") || actionLower.includes("revoked") || actionLower.includes("decline") || actionLower.includes("remove")) {
            return { keyword: "REMOVED", variant: "destructive" };
        }
        if (actionLower.includes("updated") || actionLower.includes("changed") || actionLower.includes("modified")) {
            return { keyword: "UPDATED", variant: "warning" };
        }
        if (actionLower.includes("connected")) {
            return { keyword: "CONNECTED", variant: "info" };
        }
        if (actionLower.includes("request")) {
            return { keyword: "REQUEST", variant: "default" };
        }
        if (actionLower.includes("deleted") || actionLower.includes("delete")) {
            return { keyword: "DELETED", variant: "destructive" };
        }
        if (actionLower.includes("created") || actionLower.includes("create")) {
            return { keyword: "CREATED", variant: "success" };
        }
        return null;
    };
    const getBadgeStyles = (variant: string) => {
        switch (variant) {
            case "success":
                return "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800";
            case "destructive":
                return "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
            case "warning":
                return "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
            case "info":
                return "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800";
            default:
                return "bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-800";
        }
    };


    useEffect(() => {
        if (!selectedTenant) return;
        fetchPlatformLogs(selectedTenant);
    }, [selectedTenant]);

    useEffect(() => {
        writePersistedListState(LIST_STATE_KEYS.platformAuditLogs, {
            currentPage,
            searchTerm,
            selectedTenant,
            startDate: serializePersistedDate(startDate),
            endDate: serializePersistedDate(endDate),
        });
    }, [currentPage, searchTerm, selectedTenant, startDate, endDate]);

    useEffect(() => {
        if (skipFilterPageResetRef.current) {
            skipFilterPageResetRef.current = false;
            return;
        }
        setCurrentPage(1);
    }, [searchTerm, startDate, endDate]);

    // Filter logs safely
    const filteredLogs = serverAuditLogs.filter(log => {
        const matchesSearch =
            searchTerm === "" ||
            (log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.loggedInUserEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.computerUserEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.computerName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
            (log.resourceName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);

        let matchesDateRange = true;
        if (startDate || endDate) {
            try {
                const logDate = log.time ? new Date(log.time) : null;
                if (logDate) {
                    logDate.setHours(0, 0, 0, 0);
                    if (startDate) {
                        const start = new Date(startDate);
                        start.setHours(0, 0, 0, 0);
                        if (logDate < start) matchesDateRange = false;
                    }
                    if (endDate && matchesDateRange) {
                        const end = new Date(endDate);
                        end.setHours(23, 59, 59, 999);
                        if (logDate > end) matchesDateRange = false;
                    }
                }
            } catch {
                matchesDateRange = false;
            }
        }

        return matchesSearch && matchesDateRange;
    });

    const formatTime = (timeString?: string) => {
        if (!timeString) return "-";
        try {
            const date = new Date(timeString);
            return date.toLocaleString();
        } catch {
            return timeString;
        }
    };

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;
    const paginatedLogs = filteredLogs.slice(startIndex, startIndex + PAGE_SIZE);

    useEffect(() => {
        tableScrollRef.current?.scrollTo({ top: 0 });
    }, [safeCurrentPage]);

    const handleRefresh = async () => {
        if (!selectedTenant) return;
        setCurrentPage(1);
        setSearchTerm("");
        setStartDate(undefined);
        setEndDate(undefined);
        await fetchPlatformLogs(selectedTenant);
    };

    const hasActiveFilters = Boolean(searchTerm || startDate || endDate);

    return (
        <div className="endpoint-overview endpoint-data-sections endpoint-audit-logs endpoint-audit-page endpoint-platform-logs">
            <div className="overview-card endpoint-section-toolbar">
                <div className="endpoint-toolbar-fields flex items-center gap-4">
                    <TenantLists
                        onSelectorChange={(tenant: string) => {
                            runTenantChangeWithPageReset(() => setSelectedTenant(tenant));
                        }}
                        isLabelNeeded
                        variant="card"
                    />

                    <div className="endpoint-filter-field endpoint-filter-search">
                        <span className="endpoint-filter-label"><Search />Search</span>
                        <div className="relative">
                            <Search className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                aria-label="Search platform audit logs"
                                data-testid="input-search-logs"
                            />
                        </div>
                    </div>

                    <div className="endpoint-filter-field">
                        <span className="endpoint-filter-label"><CalendarIcon />Start date</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "w-40 justify-start text-left font-normal",
                                        !startDate && "text-muted-foreground"
                                    )}
                                    data-testid="button-start-date"
                                >
                                    <CalendarIcon className="w-4 h-4 mr-2" />
                                    {startDate ? format(startDate, "MMM dd") : "Start date"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Calendar
                                    mode="single"
                                    selected={startDate}
                                    onSelect={(date) => {
                                        setStartDate(date);
                                        setEndDate(undefined);
                                    }}
                                    initialFocus
                                    disabled={(date) => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        return date < today;
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    <div className="endpoint-filter-field">
                        <span className="endpoint-filter-label"><CalendarIcon />End date</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "w-40 justify-start text-left font-normal",
                                        !endDate && "text-muted-foreground"
                                    )}
                                    data-testid="button-end-date"
                                >
                                    <CalendarIcon className="w-4 h-4 mr-2" />
                                    {endDate ? format(endDate, "MMM dd") : "End date"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                                <Calendar
                                    mode="single"
                                    selected={endDate}
                                    onSelect={setEndDate}
                                    initialFocus
                                    disabled={(date) => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        if (date < today) return true;
                                        if (!startDate) return false;
                                        return date <= startDate;
                                    }}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <div className="endpoint-toolbar-actions flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={handleRefresh}
                        disabled={loading || !selectedTenant}
                        data-testid="button-refresh-logs"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            <Card className="overview-card endpoint-section-table-card">
                <OverviewCardHeader
                    fromdetails
                    title="Platform Audit Logs"
                    description="Activity performed by admins and users in the Whiteswan platform"
                    icon={FileText}
                    detail={!loading ? `${filteredLogs.length.toLocaleString()} ${hasActiveFilters ? "matching" : "entries"}` : undefined}
                />
                <CardContent className="overview-card-content endpoint-table-content">
                    {!loading && filteredLogs.length === 0 ? (
                        <EmptyStateCard
                            icon={FileText}
                            embedded
                            title={hasActiveFilters ? "No logs match your filters" : "No platform audit logs found"}
                            description={
                                hasActiveFilters
                                    ? "Try adjusting your search terms or date range."
                                    : "Platform activity logs will appear here once actions are recorded."
                            }
                        />
                    ) : (
                        <div ref={tableScrollRef} className="endpoint-audit-scroll overflow-auto">
                            <Table className="endpoint-data-table w-full table-fixed">
                                <TableHeader className="sticky top-0 z-10">
                                    <TableRow>
                                        <TableHead className="w-[13%]">Time</TableHead>
                                        <TableHead className="w-[21%]">User / Computer User</TableHead>
                                        <TableHead className="w-[14%]">Computer Name</TableHead>
                                        <TableHead className="w-[12%]">Resource</TableHead>
                                        <TableHead className="w-[10%]">Action</TableHead>
                                        <TableHead className="w-[30%]">Details</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                                            <TableRow key={`loading-${idx}`}>
                                                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-36" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : paginatedLogs.map((log) => {
                                        const actionKeyword = getActionKeyword(log.action || "");

                                        return (
                                            <TableRow key={log.id} data-testid={`row-log-${log.id}`}>
                                                <TableCell className="platform-log-time">
                                                    {formatTime(log.time)}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="platform-log-stack">
                                                        {log.loggedInUserEmail && (
                                                            <div className="platform-log-primary">
                                                                <span className="platform-log-icon" data-tone="user" aria-hidden="true">
                                                                    <User />
                                                                </span>
                                                                <span className="break-all">{log.loggedInUserEmail}</span>
                                                            </div>
                                                        )}
                                                        {log.computerUserEmail && (
                                                            <div className="platform-log-secondary">
                                                                <Monitor aria-hidden="true" />
                                                                <span className="break-all">{log.computerUserEmail}</span>
                                                            </div>
                                                        )}
                                                        {!log.loggedInUserEmail && !log.computerUserEmail && (
                                                            <span className="text-muted-foreground">-</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {log.computerName ? (
                                                        <div className="platform-log-primary">
                                                            <span className="platform-log-icon" data-tone="computer" aria-hidden="true">
                                                                <Monitor />
                                                            </span>
                                                            <span className="break-all">{log.computerName}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="platform-log-stack">
                                                        {log.resourceType && (
                                                            <Badge variant="secondary" className="platform-log-type">
                                                                {log.resourceType}
                                                            </Badge>
                                                        )}
                                                        {log.resourceName && (
                                                            <div className="font-mono text-muted-foreground break-all" title={log.resourceName || ""}>
                                                                {log.resourceName}
                                                            </div>
                                                        )}
                                                        {!log.resourceType && !log.resourceName && (
                                                            <span className="text-muted-foreground">-</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {log.action && actionKeyword ? (
                                                        <Badge className={`platform-log-keyword ${getBadgeStyles(actionKeyword.variant)}`}>
                                                            {actionKeyword.keyword}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="platform-log-action">
                                                        <span className="break-words">
                                                            {log.action} {actionKeyword?.keyword ? ` ${actionKeyword.keyword}` : ""}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
                {!loading && filteredLogs.length > 0 && (
                    <div className="endpoint-table-pagination endpoint-pagination-bar border-t">
                        <span className="endpoint-pagination-summary">
                            Showing {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, filteredLogs.length)} of {filteredLogs.length.toLocaleString()} {hasActiveFilters ? "filtered entries" : "entries"} ({serverAuditLogs.length.toLocaleString()} total)
                        </span>
                        <CardPagination
                            label="Platform audit log pages"
                            currentPage={safeCurrentPage}
                            totalPages={totalPages}
                            disabled={loading}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </Card>
        </div>
    );
}
